import PropTypes from 'prop-types';
import './Form.css';

/**
 * Read-only grid for stacked label/value pairs (e.g. billing data,
 * causa metadata). Renders as a 2- or 3-column grid wrapped in the
 * standard data-card chrome (border + radius + paper-elev background).
 *
 * @param {object} props
 * @param {2|3} [props.cols=2]  Number of equal columns.
 * @param {React.ReactNode} props.children  Typically `<FieldDisplay>` cells.
 *
 * @example
 *   <FieldGrid cols={2}>
 *     <FieldDisplay label="RUT" value="11.111.111-1" mono />
 *     <FieldDisplay label="Razón social" value="Estudio Ejemplo SpA" />
 *   </FieldGrid>
 */
export function FieldGrid({ cols = 2, children }) {
  return (
    <div className={`field-grid${cols === 3 ? ' cols-3' : ''}`}>{children}</div>
  );
}

FieldGrid.propTypes = {
  cols: PropTypes.oneOf([2, 3]),
  children: PropTypes.node,
};

/**
 * Read-only labelled value cell used inside `FieldGrid`.
 *
 * @param {object} props
 * @param {string} props.label   - Plain-text label shown above the value.
 * @param {React.ReactNode} props.value
 *   Accepts any ReactNode. SECURITY: consumers MUST NOT pass server-supplied
 *   HTML strings into this slot without sanitizing — pass plain strings or
 *   trusted React children. This component does not sanitize and does not
 *   use `dangerouslySetInnerHTML`, but a downstream consumer that does will
 *   inherit any XSS risk from its data source.
 * @param {boolean} [props.mono]  - Render the value in monospace.
 * @param {boolean} [props.muted] - De-emphasize the value (secondary ink).
 */
export function FieldDisplay({ label, value, mono = false, muted = false }) {
  const valueCls = ['field-value', mono ? 'mono' : '', muted ? 'muted' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="field-display">
      <div className="field-label">{label}</div>
      <div className={valueCls}>{value}</div>
    </div>
  );
}

FieldDisplay.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  mono: PropTypes.bool,
  muted: PropTypes.bool,
};

export default FieldGrid;
